import { supabase } from '@/lib/supabase';

export const DEFAULT_FACILITY_SESSION_TIMEOUT_MINUTES = 60;

const MIN_FACILITY_SESSION_TIMEOUT_MINUTES = 5;
const MAX_FACILITY_SESSION_TIMEOUT_MINUTES = 1440;

export interface FacilitySecuritySettings {
    sessionTimeoutMinutes: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function resolveFacilitySessionTimeoutMinutes(value: unknown): number {
    const parsedValue = typeof value === 'number'
        ? value
        : typeof value === 'string'
            ? Number.parseInt(value, 10)
            : Number.NaN;

    if (!Number.isFinite(parsedValue)) {
        return DEFAULT_FACILITY_SESSION_TIMEOUT_MINUTES;
    }

    return Math.min(
        MAX_FACILITY_SESSION_TIMEOUT_MINUTES,
        Math.max(MIN_FACILITY_SESSION_TIMEOUT_MINUTES, Math.trunc(parsedValue)),
    );
}

function getFacilitySecurityRecord(value: unknown): Record<string, unknown> {
    return isRecord(value) ? value : {};
}

export interface Facility {
    id: string;
    name: string;
    facilityType: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
    totalBeds: number;
    timezone: string;
    settings: Record<string, unknown>;
    publicSlug: string | null;
    avvSignedAt: string | null;
    isActive: boolean;
}

export async function getFacility(facilityId: string): Promise<Facility | null> {
    const { data, error } = await supabase
        .from('care_facilities')
        .select('*')
        .eq('id', facilityId)
        .maybeSingle();

    if (error) throw new Error(error.message);
    if (!data) return null;

    return {
        id: data.id as string,
        name: data.name as string,
        facilityType: data.facility_type as string | null,
        address: data.address as string | null,
        phone: data.phone as string | null,
        email: data.email as string | null,
        totalBeds: (data.total_beds as number) ?? 0,
        timezone: (data.timezone as string) ?? 'Europe/Berlin',
        settings: (data.settings as Record<string, unknown>) ?? {},
        publicSlug: (data.public_slug as string | null) ?? null,
        avvSignedAt: (data.avv_signed_at as string | null) ?? null,
        isActive: data.is_active === undefined ? true : Boolean(data.is_active),
    };
}

export async function getFacilitySecuritySettings(facilityId: string): Promise<FacilitySecuritySettings> {
    const { data, error } = await supabase
        .from('care_facility_settings')
        .select('security')
        .eq('facility_id', facilityId)
        .maybeSingle();

    if (error) throw new Error(error.message);

    const security = getFacilitySecurityRecord(data?.security);

    return {
        sessionTimeoutMinutes: resolveFacilitySessionTimeoutMinutes(security.sessionTimeoutMinutes),
    };
}

export async function updateFacilitySettings(
    facilityId: string,
    settings: Partial<Pick<Facility, 'name' | 'facilityType' | 'settings'>>
): Promise<void> {
    const payload: Record<string, unknown> = {};

    if (settings.name !== undefined) {
        payload.name = settings.name;
    }

    if (settings.facilityType !== undefined) {
        payload.facility_type = settings.facilityType;
    }

    if (settings.settings !== undefined) {
        payload.settings = settings.settings;
    }

    if (Object.keys(payload).length === 0) {
        return;
    }

    const { error } = await supabase
        .from('care_facilities')
        .update(payload)
        .eq('id', facilityId);

    if (error) throw new Error(error.message);
}

export async function updateFacilitySecuritySettings(
    facilityId: string,
    settings: Partial<FacilitySecuritySettings>,
): Promise<FacilitySecuritySettings> {
    const { data, error: readError } = await supabase
        .from('care_facility_settings')
        .select('security')
        .eq('facility_id', facilityId)
        .maybeSingle();

    if (readError) throw new Error(readError.message);

    const currentSecurity = getFacilitySecurityRecord(data?.security);
    const nextSecurity: Record<string, unknown> = {
        ...currentSecurity,
    };

    if (settings.sessionTimeoutMinutes !== undefined) {
        nextSecurity.sessionTimeoutMinutes = resolveFacilitySessionTimeoutMinutes(settings.sessionTimeoutMinutes);
    }

    const { error } = await supabase
        .from('care_facility_settings')
        .upsert(
            {
                facility_id: facilityId,
                security: nextSecurity,
            },
            { onConflict: 'facility_id' },
        );

    if (error) throw new Error(error.message);

    return {
        sessionTimeoutMinutes: resolveFacilitySessionTimeoutMinutes(nextSecurity.sessionTimeoutMinutes),
    };
}

export async function getCareStaff(facilityId: string) {
    const { data, error } = await supabase
        .from('care_staff')
        .select('id, user_id, role, display_name, email, is_active')
        .eq('facility_id', facilityId)
        .eq('is_active', true)
        .order('display_name');

    if (error) throw new Error(error.message);
    return data ?? [];
}
